import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { FaPlay, FaStar, FaLock, FaCheck, FaBookOpen, FaClock, FaGraduationCap, FaTrophy, FaFire, FaRocket, FaHeart } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './AlphabetLearningPathway.css';

interface VocabularyItem {
  id: string;
  persian: string;
  english: string;
  transliteration: string;
  icon: string;
  iconType: 'svg' | 'base64';
  audioUrl?: string;
  pronunciation?: string;
  example?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category?: string;
  tags?: string[];
}

interface GridAlphabetLesson {
  id: string;
  type: string;
  title: string;
  description: string;
  order: number;
  content: {
    letter: string;
    letterName: string;
    letterNameEnglish: string;
    letterNamePersian: string;
    level: string;
    difficulty: string;
    topics: string[];
    styleTags: string[];
    learningGoals: string[];
    vocabularyItems: VocabularyItem[];
    activities: {
      matching: {
        enabled: boolean;
        pairs: Array<{
          persian: string;
          english: string;
          transliteration: string;
        }>;
      };
      multipleChoice: {
        enabled: boolean;
        questions: Array<{
          question: string;
          options: string[];
          correctAnswer: string;
          explanation: string;
        }>;
      };
      pronunciation: {
        enabled: boolean;
        words: string[];
      };
      writing: {
        enabled: boolean;
        practiceWords: string[];
      };
    };
    completionCriteria: {
      vocabularyMastered: number;
      activitiesCompleted: number;
      timeSpent: number;
    };
    estimatedDuration: number;
  };
  isPremium?: boolean;
  category?: string;
  level?: string;
}

interface Pathway {
  _id: string;
  title: string;
  description: string;
  lessonCount: number;
  level: string;
  category: string;
  lessons: GridAlphabetLesson[];
}

const AlphabetLearningPathway: React.FC = () => {
  const [completedLessons, setCompletedLessons] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<'all' | 'easy' | 'medium' | 'hard'>('all');
  const [sortBy, setSortBy] = useState<'order' | 'difficulty' | 'progress'>('order');
  const [showCompletedOnly, setShowCompletedOnly] = useState(false);

  console.log('🔍 AlphabetLearningPathway component rendered');

  // Fetch the alphabet learning pathway
  const { data: pathwayData, isLoading, error } = useQuery({
    queryKey: ['alphabet-learning-pathway'],
    queryFn: async () => {
      console.log('🔍 Fetching alphabet learning pathway...');
      const response = await axios.get(`${API_BASE_URL}/api/pathways/alphabet-learning`);
      console.log('🔍 API response:', response.data);
      return response.data.data as Pathway;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  console.log('🔍 Component state:', { isLoading, error, pathwayData });

  // Load completed lessons from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('completedAlphabetLessons');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate that all completed lesson IDs exist in the pathway
      if (pathwayData) {
        const validLessonIds = pathwayData.lessons.map(lesson => lesson.id);
        const validCompletedLessons = parsed.filter((lessonId: string) => validLessonIds.includes(lessonId));
        
        // If there are invalid lesson IDs, update localStorage
        if (validCompletedLessons.length !== parsed.length) {
          localStorage.setItem('completedAlphabetLessons', JSON.stringify(validCompletedLessons));
          console.log('🔍 Cleaned up invalid lesson IDs from localStorage');
        }
        
        setCompletedLessons(validCompletedLessons);
        console.log('🔍 Loaded completed lessons from localStorage:', validCompletedLessons);
      } else {
        setCompletedLessons(parsed);
        console.log('🔍 Loaded completed lessons from localStorage:', parsed);
      }
    }
  }, [pathwayData]);

  // Debug: Log all lesson IDs when pathway data is loaded
  useEffect(() => {
    if (pathwayData) {
      console.log('🔍 Pathway lessons:', pathwayData.lessons.map((lesson, index) => ({
        index,
        id: lesson.id,
        title: lesson.title,
        order: lesson.order
      })));
    }
  }, [pathwayData]);

  // Helper functions for filtering and sorting
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return <FaRocket className="text-green-500" />;
      case 'medium': return <FaFire className="text-yellow-500" />;
      case 'hard': return <FaTrophy className="text-red-500" />;
      default: return <FaBookOpen className="text-gray-500" />;
    }
  };

  const getProgressPercentage = () => {
    if (!pathwayData) return 0;
    return Math.round((completedLessons.length / pathwayData.lessons.length) * 100);
  };

  const getStreakCount = () => {
    // Calculate consecutive completed lessons from the beginning
    let streak = 0;
    for (let i = 0; i < pathwayData?.lessons.length; i++) {
      if (completedLessons.includes(pathwayData.lessons[i].id)) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const filteredAndSortedLessons = () => {
    if (!pathwayData) return [];
    
    let filtered = pathwayData.lessons.filter(lesson => {
      const difficultyMatch = selectedDifficulty === 'all' || lesson.content.difficulty === selectedDifficulty;
      const completedMatch = !showCompletedOnly || completedLessons.includes(lesson.id);
      return difficultyMatch && completedMatch;
    });

    // Sort lessons
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'difficulty':
          const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
          return difficultyOrder[a.content.difficulty as keyof typeof difficultyOrder] - 
                 difficultyOrder[b.content.difficulty as keyof typeof difficultyOrder];
        case 'progress':
          const aCompleted = completedLessons.includes(a.id);
          const bCompleted = completedLessons.includes(b.id);
          return aCompleted === bCompleted ? 0 : aCompleted ? 1 : -1;
        default:
          return a.order - b.order;
      }
    });

    return filtered;
  };

  const getLessonProgress = (lessonId: string): number => {
    return completedLessons.includes(lessonId) ? 100 : 0;
  };

  const isLessonAccessible = (lesson: GridAlphabetLesson, index: number): boolean => {
    if (index === 0) return true;
    const previousLesson = pathwayData?.lessons[index - 1];
    const isAccessible = previousLesson ? completedLessons.includes(previousLesson.id) : false;
    console.log(`🔍 Lesson ${index} (${lesson.id}) accessibility check:`, {
      previousLessonId: previousLesson?.id,
      completedLessons,
      isAccessible
    });
    return isAccessible;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Alphabet Learning Pathway...</p>
        </div>
      </div>
    );
  }

  if (error || !pathwayData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📚</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Pathway Not Found</h2>
          <p className="text-gray-600 mb-4">The Alphabet Learning pathway could not be loaded.</p>
          <Link to="/pathways" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
            Back to Pathways
          </Link>
        </div>
      </div>
    );
  }

  const pathway = pathwayData;
  const totalLessons = pathway.lessons.length;
  const completedCount = completedLessons.length;
  // Fix: Ensure progress percentage never exceeds 100%
  const progressPercentage = totalLessons > 0 ? Math.min((completedCount / totalLessons) * 100, 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="relative">
            <h1 className="text-4xl md:text-6xl font-bold text-gradient mb-4">
              {pathway.title}
            </h1>
            <div className="absolute -top-2 -right-2 text-4xl">📚</div>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {pathway.description}
          </p>
          
          {/* Enhanced Progress Overview */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-100 to-emerald-100 rounded-full translate-y-12 -translate-x-12"></div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                    <FaGraduationCap className="text-xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Your Learning Journey</h3>
                    <p className="text-gray-600 text-lg">{completedCount} of {totalLessons} lessons completed</p>
                    {getStreakCount() > 0 && (
                      <div className="flex items-center mt-2 text-orange-600">
                        <FaFire className="mr-1" />
                        <span className="font-semibold">{getStreakCount()} lesson streak!</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-gradient mb-2">{Math.round(progressPercentage)}%</div>
                  <div className="text-sm text-gray-500 uppercase tracking-wide">Complete</div>
                </div>
              </div>
              
              {/* Enhanced Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div 
                  className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 h-4 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                  style={{ width: `${progressPercentage}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
                </div>
              </div>
              
              {/* Progress milestones */}
              <div className="flex justify-between text-sm text-gray-500">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Filter and Sort Controls */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Filter by difficulty:</label>
                <select 
                  value={selectedDifficulty} 
                  onChange={(e) => setSelectedDifficulty(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Levels</option>
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Sort by:</label>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="order">Order</option>
                  <option value="difficulty">Difficulty</option>
                  <option value="progress">Progress</option>
                </select>
              </div>
              
              <label className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  checked={showCompletedOnly}
                  onChange={(e) => setShowCompletedOnly(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Show completed only</span>
              </label>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaBookOpen className="text-xl text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Lessons</h3>
            <p className="text-4xl font-bold text-green-600">{totalLessons}</p>
            <p className="text-sm text-gray-500 mt-1">All Persian letters</p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaClock className="text-xl text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Duration</h3>
            <p className="text-4xl font-bold text-blue-600">
              {pathway.lessons.reduce((total, lesson) => total + (lesson.content?.estimatedDuration || 0), 0)} min
            </p>
            <p className="text-sm text-gray-500 mt-1">Estimated time</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaStar className="text-xl text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Level</h3>
            <p className="text-4xl font-bold text-purple-600">{pathway.level}</p>
            <p className="text-sm text-gray-500 mt-1">Difficulty level</p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-red-100 rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <FaTrophy className="text-xl text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Streak</h3>
            <p className="text-4xl font-bold text-orange-600">{getStreakCount()}</p>
            <p className="text-sm text-gray-500 mt-1">Lessons in a row</p>
          </div>
        </div>

        {/* Enhanced Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredAndSortedLessons().map((lesson, index) => {
            const progress = getLessonProgress(lesson.id);
            const isCompleted = progress === 100;
            const isAccessible = isLessonAccessible(lesson, index);
            const isLocked = !isAccessible;
            
            return (
              <div 
                key={lesson.id}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-105 relative ${
                  isCompleted ? 'ring-2 ring-green-500 bg-gradient-to-br from-green-50 to-emerald-50' : ''
                } ${isLocked ? 'opacity-70' : ''}`}
              >
                {/* Completion badge */}
                {isCompleted && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <FaCheck className="text-white text-sm" />
                    </div>
                  </div>
                )}

                {/* Lock overlay */}
                {isLocked && (
                  <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-20 rounded-2xl">
                    <div className="text-center text-white">
                      <FaLock className="text-xl mx-auto mb-2" />
                      <p className="text-sm font-medium">Complete previous lesson</p>
                    </div>
                  </div>
                )}
                {/* Lesson Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                        <span className="text-white text-3xl font-bold letter-display">
                          {lesson.content?.letter || '?'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {lesson.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {lesson.content?.letterNameEnglish} ({lesson.content?.letterNamePersian})
                        </p>
                        <div className="flex items-center mt-1">
                          {getDifficultyIcon(lesson.content?.difficulty || 'easy')}
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(lesson.content?.difficulty || 'easy')}`}>
                            {lesson.content?.difficulty || 'easy'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {isCompleted && (
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <FaCheck className="text-white text-sm" />
                      </div>
                    )}
                    {isLocked && (
                      <div className="w-8 h-8 bg-orange-200 rounded-full flex items-center justify-center">
                        <FaLock className="text-orange-600 text-sm" />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {lesson.description}
                  </p>

                  {/* Enhanced Vocabulary Preview */}
                  {lesson.content?.vocabularyItems && lesson.content.vocabularyItems.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <FaBookOpen className="mr-1 text-blue-500" />
                        Vocabulary Preview:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {lesson.content.vocabularyItems.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 text-xs rounded-full border border-blue-200 hover:shadow-sm transition-all duration-200">
                            <span className="font-medium">{item.persian}</span>
                            <span className="text-blue-600 ml-1">({item.english})</span>
                          </div>
                        ))}
                        {lesson.content.vocabularyItems.length > 3 && (
                          <span className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 text-xs rounded-full border border-gray-300">
                            +{lesson.content.vocabularyItems.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Lesson Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center">
                      <FaClock className="mr-1" />
                      {lesson.content?.estimatedDuration || 15} min
                    </span>
                    <span className="flex items-center">
                      <FaBookOpen className="mr-1" />
                      {lesson.content?.vocabularyItems?.length || 0} words
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Enhanced Action Button */}
                <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50">
                  {isAccessible ? (
                    <Link
                      to={`/pathways/alphabet-learning-pathway/lesson/${lesson.id}`}
                      className={`w-full py-4 rounded-xl font-semibold text-center block flex items-center justify-center gap-3 transition-all duration-300 transform hover:scale-105 shadow-lg ${
                        isCompleted 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700' 
                          : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                      }`}
                    >
                      {isCompleted ? (
                        <>
                          <FaCheck className="text-lg" />
                          <span>Review Lesson</span>
                          <FaHeart className="text-lg" />
                        </>
                      ) : (
                        <>
                          <FaPlay className="text-lg" />
                          <span>Start Lesson</span>
                          <FaRocket className="text-lg" />
                        </>
                      )}
                    </Link>
                  ) : (
                    <button
                      disabled
                      className="w-full py-4 bg-gradient-to-r from-gray-300 to-gray-400 text-gray-600 rounded-xl cursor-not-allowed font-semibold flex items-center justify-center gap-3 shadow-lg"
                    >
                      <FaLock className="text-lg" />
                      <span>Complete Previous Lesson</span>
                    </button>
                  )}
                  
                  {/* Additional lesson info */}
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                    <span>Lesson {lesson.order}</span>
                    <span>{lesson.content?.vocabularyItems?.length || 0} words</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4 text-left text-white">Ready to Master the Persian Alphabet?</h2>
            <p className="text-white mb-6 max-w-2xl mx-auto text-left">
              Start with the first lesson and build your foundation in Persian reading and writing. 
              Each lesson includes interactive activities, pronunciation practice, and vocabulary building.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={`/pathways/alphabet-learning-pathway/lesson/${pathway.lessons[0]?.id}`}
                className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                <FaPlay className="mr-2" />
                Start Learning Now
              </Link>
              <Link
                to="/alphabet-grid"
                className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition"
              >
                <FaStar className="mr-2" />
                View Alphabet Grid
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlphabetLearningPathway; 